import { Injectable, Signal, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DOCUMENT } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { defaultConfig } from 'src/app/app.config';

/**
 * Preferencias de presentación de la aplicación, persistidas en `localStorage`.
 *
 * Reemplaza al store de `@ngrx/store`, que existía únicamente para esto: una bolsa de ajustes
 * (`settings`) con una sola acción. Eran 97 líneas repartidas en cinco archivos —`store/reducers.ts`,
 * `store/settings/actions.ts`, `store/settings/reducers.ts` y sus dos specs— para guardar y leer
 * banderas de layout. Quitar ngrx desbloquea además Angular 22, cuyo peer `@ngrx/store` todavía no
 * admite.
 *
 * La fuente de verdad es un `BehaviorSubject` y no una señal, a propósito: los ocho consumidores se
 * suscriben durante `ngOnInit` y dependen de recibir el valor actual **de forma sincrónica**, tal
 * como lo hacía `store.pipe(select(...))`. Con `toObservable()` sobre una señal la primera emisión
 * llega en un microtask posterior, y los campos quedarían en `undefined` durante un ciclo.
 * `settings` expone la misma información como señal para el código nuevo.
 */

/**
 * Ajustes conocidos, con sus tipos.
 *
 * Antes esto era `Store<any>` y el estado viajaba sin tipo, así que un `state.isMenuColapsed` mal
 * escrito no lo detectaba nadie. Se declaran explícitamente porque el conjunto es cerrado: lo
 * define `defaultConfig.settings` (más `logo`, que sale de `companyName`).
 *
 * `isTabletView` no está en `defaultConfig.settings` pero sí lo escribe `app.component` al detectar
 * el viewport; se declara para que quede a la vista, y por eso todas las claves son opcionales.
 */
export interface AppSettings {
  logo?: string;
  locale?: string;
  theme?: string;
  primaryColor?: string;
  menuColor?: string;
  menuLayoutType?: string;
  authPagesColor?: string;
  routerAnimation?: string;
  leftMenuWidth?: number;
  isSidebarOpen?: boolean;
  isSupportChatOpen?: boolean;
  isMobileView?: boolean;
  isTabletView?: boolean;
  isMobileMenuOpen?: boolean;
  isMenuCollapsed?: boolean;
  isMenuUnfixed?: boolean;
  isMenuShadow?: boolean;
  isTopbarFixed?: boolean;
  isGrayTopbar?: boolean;
  isContentMaxWidth?: boolean;
  isAppMaxWidth?: boolean;
  isGrayBackground?: boolean;
  isCardShadow?: boolean;
  isSquaredBorders?: boolean;
  isBorderless?: boolean;

  /**
   * Estas tres las lee el panel de ajustes pero nadie las escribe, y tampoco están en
   * `defaultConfig.settings`: con el store de ngrx también llegaban como `undefined`. Se declaran
   * para que el tipo refleje lo que el código realmente consulta, y para dejar a la vista que son
   * lecturas muertas —candidatas a eliminarse junto con los campos que las reciben en
   * `sidebar.component`—.
   */
  flyoutMenuColor?: string;
  systemLayoutColor?: string;
  isFooterDark?: boolean;
}

/** Prefijo con el que cada ajuste vive en `localStorage`. Lo fijaba el reducer anterior. */
const PREFIJO = 'app.settings.';

@Injectable({ providedIn: 'root' })
export class AppSettingsService {
  private readonly document = inject(DOCUMENT);

  private readonly estado: BehaviorSubject<AppSettings>;

  /** Emite el estado completo, empezando por el actual. Equivale al `select(getSettings)` anterior. */
  readonly changes$: Observable<AppSettings>;

  /** El mismo estado como señal, para el código que ya no quiera suscribirse. */
  readonly settings: Signal<AppSettings>;

  constructor() {
    this.estado = new BehaviorSubject<AppSettings>(this.leerPersistidos());
    this.changes$ = this.estado.asObservable();
    this.settings = toSignal(this.estado, { requireSync: true });
  }

  /** El valor actual sin suscribirse. */
  get actual(): AppSettings {
    return this.estado.value;
  }

  /**
   * Aplica cambios y los persiste.
   *
   * Sustituye a `dispatch(new SetStateAction({ clave: valor }))`. El reducer anterior mezclaba
   * todas las claves recibidas pero solo persistía la primera; aquí se persisten todas. En la
   * práctica no cambia nada, porque los nueve sitios que lo llamaban pasaban una sola clave.
   */
  set(cambios: AppSettings): void {
    Object.entries(cambios).forEach(([clave, valor]) => {
      try {
        this.almacenamiento?.setItem(`${PREFIJO}${clave}`, JSON.stringify(valor));
      } catch {
        // Cuota llena o almacenamiento bloqueado: la preferencia no sobrevive a la recarga, pero
        // no tiene por qué impedir que el usuario abra el menú.
      }
    });
    this.estado.next({ ...this.estado.value, ...cambios });
  }

  /**
   * Aplica un ajuste cuya clave se conoce solo en ejecución.
   *
   * Existe para los dos sitios que la reciben como dato: el interruptor genérico del panel de
   * ajustes y los parámetros de la URL que `app.component` traslada al estado.
   */
  setKey(clave: string, valor: unknown): void {
    this.set({ [clave]: valor } as AppSettings);
  }

  /**
   * Estado inicial: lo guardado en `localStorage` y, para lo que falte, el valor por omisión.
   *
   * Solo se leen las claves conocidas por `defaultConfig`, igual que antes: así una clave vieja
   * que quedó en el navegador no reaparece en el estado.
   */
  private leerPersistidos(): AppSettings {
    const porOmision: Record<string, unknown> = {
      logo: defaultConfig.companyName,
      ...defaultConfig.settings,
    };
    const ajustes: Record<string, unknown> = {};

    Object.keys(porOmision).forEach(clave => {
      ajustes[clave] = this.leerClave(clave, porOmision[clave]);
    });

    return ajustes as AppSettings;
  }

  private leerClave(clave: string, porOmision: unknown): unknown {
    const crudo = this.almacenamiento?.getItem(`${PREFIJO}${clave}`) ?? null;
    if (crudo === null) {
      return porOmision;
    }

    try {
      return JSON.parse(crudo);
    } catch {
      // Un valor corrupto no debe impedir que la aplicación arranque.
      return porOmision;
    }
  }

  /** `localStorage` puede no existir (modo privado con almacenamiento bloqueado, o SSR). */
  private get almacenamiento(): Storage | null {
    try {
      return this.document.defaultView?.localStorage ?? null;
    } catch {
      return null;
    }
  }
}
